import { i18nFunct, useSiteMetadata } from '@lucid/gatsby-theme-lucid';
import { graphql, useStaticQuery } from 'gatsby';
import React, { useContext } from 'react';

import Link from 'components/link/link';
import LayoutContext from 'contexts/layoutContext';
import { FileConnection, TableJsonConnection } from 'generated/graphql';
import useMarketoLink from 'hooks/use-marketo-link';
import './table.scss';

const Table = () => {
  const { language } = useContext(LayoutContext);
  const getMarketoLink = useMarketoLink(language);
  const { productUrl } = useSiteMetadata();

  const { i18nKey, urlLangPrefix } = language;

  const {
    tableContent,
    i18nData
  }: {
    tableContent: TableJsonConnection;
    i18nData: FileConnection;
  } = useStaticQuery(
    graphql`
      {
        tableContent: allTableJson {
          edges {
            node {
              id
              rowType
              cell1
              cell2
              cell3
              newIntegration
              link
            }
          }
        }
        i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
          edges {
            node {
              name
              childSrcJson {
                mktg_partner_view_info
                mktg_ct_edu_r1_c1
                mktg_ct_edu_r1_c2
                mktg_ct_edu_r1_c3
                mktg_ct_edu_r2_c1
                mktg_ct_edu_r2_c2
                mktg_ct_edu_r2_c3
                mktg_ct_edu_r3_c1
                mktg_ct_edu_r3_c2
                mktg_ct_edu_unlimited
                mktg_ct_edu_r4_c1
                mktg_ct_edu_r4_c2
                mktg_ct_edu_r5_c1
                mktg_ct_edu_r5_c2
                mktg_ct_edu_r6_c1
                mktg_ct_edu_r6_c2
                mktg_ct_edu_r7_c1
                mktg_ct_edu_r7_c2
                mktg_ct_edu_r8_c1
                mktg_ct_edu_r9_c1
                mktg_ct_edu_r9_c2
                mktg_ct_edu_r10_c1
                mktg_ct_edu_r11_c1
                mktg_ct_edu_r12_c1
                mktg_ct_edu_r13_c1
                mktg_ct_edu_r14_c1
                mktg_ct_edu_r15_c1
                mktg_ct_edu_r16_c1
                mktg_ct_edu_r17_c1
                mktg_ct_edu_r18_c1
                mktg_ct_edu_r19_c1
                mktg_ct_edu_r20_c1
                mktg_ct_edu_r21_c1
                mktg_ct_edu_r22_c1
                mktg_ct_edu_r23_c1
                mktg_ct_edu_r24_c1
                mktg_ct_edu_r25_c1
                mktg_ct_edu_r26_c1
                mktg_ct_edu_r28_c1
                mktg_ct_edu_r29_c1
                mktg_ct_edu_r30_c1
                mktg_ct_edu_r31_c1
                mktg_ct_edu_r32_c1
                mktg_ct_edu_r33_c1
                mktg_ct_edu_r34_c1
                mktg_ct_edu_r35_c1
                mktg_ct_edu_new
              }
            }
          }
        }
      }
    `
  );

  const i18n = i18nFunct(i18nData, i18nKey);

  const { url, copy } = getMarketoLink('1056')!;

  return (
    <section className="table">
      <table className="lucid-container">
        <tbody className="table__body">
          <tr className="table__row  table__row--dark table__row--rounded">
            <th className="table__cell  table__cell--header">
              {i18n('mktg-ct-edu-r1-c1')}
            </th>
            <th className="table__cell  table__cell--header">
              {i18n('mktg-ct-edu-r1-c2')}
            </th>
            <th className="table__cell  table__cell--header">
              {i18n('mktg-ct-edu-r1-c3')}
            </th>
          </tr>

          <tr className="table__row  table__row--light">
            <td className="table__cell  cell--info">
              {i18n('mktg-ct-edu-r2-c1')}
            </td>
            <td className="table__cell  cell--info">
              <Link
                asButton
                primary
                to={`${productUrl}/${urlLangPrefix}users/registerLevel`}
              >
                {i18n('mktg-ct-edu-r2-c2')}
              </Link>
            </td>
            <td className="table__cell  cell--info">
              <Link asButton primary to={url}>
                {i18n('mktg-ct-edu-r2-c3') || copy}
              </Link>
              {' '}
            </td>
          </tr>

          {tableContent.edges.map(
            (
              { node: { cell1, cell2, cell3, newIntegration, rowType, link } },
              index
            ) =>
              rowType === 'header' ? (
                <tr
                  key={`${rowType}-${index}`}
                  className="table__row  table__row--dark"
                >
                  <th className="table__cell  table__cell--header">
                    {i18n(`${cell1!}`)}
                    {' '}
                  </th>
                  <th className="table__cell  table__cell--header">
                    {i18n(`${cell2!}`)}
                  </th>
                  <th className="table__cell  table__cell--header">
                    {i18n(`${cell3!}`)}
                  </th>
                </tr>
              ) : (
                  /* eslint-disable-next-line react/jsx-indent */
                  <tr
                    key={`row-${index}`}
                    className="table__row   table__row--light"
                  >
                    <td className="table__cell  table__cell--info">
                      {link! === 'false' && i18n(`${cell1!}`)}
                      {link! !== 'false' && (
                        <a className="table__link--chameleon" href={link!}>
                          {i18n(`${cell1}`)}
                        </a>
                      )}

                      {newIntegration! === 'true' && (
                        <span className="table__cell--new">
                          {i18n('mktg-ct-edu-new')}
                        </span>
                      )}
                    </td>
                    <td className="table__cell  table__cell--info">
                      {cell2! === 'true' && (
                        <img
                          alt="checkmark"
                          className="table__checkmark"
                          src="//d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/images/common/lucid-checkmark.png"
                        />
                      )}
                      {cell2! !== 'true' && i18n(`${cell2}`)}
                    </td>
                    <td className="table__cell  table__cell--info">
                      {cell3! === 'true' && (
                        <img
                          alt="checkmark"
                          className="table__checkmark"
                          src="//d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/images/common/lucid-checkmark.png"
                        />
                      )}
                      {cell3! !== 'true' && i18n(`${cell3}`)}
                    </td>
                  </tr>
                )
          )}
        </tbody>
      </table>
    </section>
  );
};

export default Table;
